import { type ReactElement, useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID } from '@elfsight/lib-utils';
import { FormProvider } from '@elfsight/widget-form-builder/widget';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  useFontsLoader
} from '@elfsight/widget-ui';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import {
  API_STATIC_ENDPOINTS_SCHEMA,
  API_SUBMISSION_FORM_SERVICE_NAME
} from './constants';
import { meta } from './meta';
import type { CalculatorSettings } from './types';
import { CalculationsProvider } from './widget/calculations-context';
import { ConditionalLogicProvider } from './widget/conditional-logic-context';
import { EmailSubmitPopupProvider } from './widget/use-email-submit-popup';
import { useHasSubmissionForm } from './widget/use-has-submission-form';
import { Widget } from './widget/widget';

export function Calculator(_settings: CalculatorSettings) {
  return (
    <CalculatorProviders>
      <Widget />
    </CalculatorProviders>
  );
}

export function CalculatorProviders({ children }: { children: ReactElement }) {
  const {
    customCSS,
    customJS,
    font,
    editMode,
    widgetId,
    renderThumbnailMode,
    textColor,
    width,
    language
  } = useSettings();
  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    meta.alias,
    widgetId
  );

  const i18n = useMemo(() => i18nInit(), []);

  const hasSubmissionForm = useHasSubmissionForm();

  useDefineAPISchema({
    ...API_STATIC_ENDPOINTS_SCHEMA,
    [API_SUBMISSION_FORM_SERVICE_NAME]: hasSubmissionForm
  });
  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  const messages = useLoadLocale(language?.language, 'widget', i18n);
  useLanguageControlOverrides({ language, messages, catalog: CATALOG, i18n });

  return (
    <I18nProvider i18n={i18n}>
      <PortalNestProvider customCSSClassName={customCSSPrefixClassName}>
        <EmailSubmitPopupProvider>
          <FormProvider widgetId={widgetId || LOCAL_WIDGET_ID}>
            <ConditionalLogicProvider>
              <CalculationsProvider>
                <CustomCSS
                  customCSS={customCSS}
                  prefixClassName={customCSSPrefixClassName}
                />

                <EmbedRoot
                  customCSSClassName={customCSSPrefixClassName}
                  font={font}
                  textColor={textColor}
                  width={width}
                >
                  {children}
                </EmbedRoot>
              </CalculationsProvider>
            </ConditionalLogicProvider>
          </FormProvider>
        </EmailSubmitPopupProvider>
      </PortalNestProvider>
    </I18nProvider>
  );
}
